import { AxiosProgressEvent, AxiosError } from 'axios';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
// import { SocketContext } from '../context/SocketProvider';
// import { ISocketContext } from '../types/socketclient';
import useAxiosPrivate from './useAxiosPrivate';

interface UploadResult {
  success: boolean;
  message?: string;
  error?: string;
}

interface DownloadResult {
  success: boolean;
  data?: Blob;
  error?: string;
}

interface DeleteResult {
  success: boolean;
  error?: string;
}

interface GetResult {
  success: boolean;
  data?: string[];
  error?: string;
}

export interface StandardReport {
  files: File[];
}

export interface ExpoCompilingReport {
  invoiceNumber: string;
  invoiceDate: Date;
  files: File[];
}

export interface ScormWrapper {
  files: File[];
  title: string;
}

export interface VttTranslationReport {
  files: File[];
  languages: string[];
}

type ReportType =
  | StandardReport
  | ExpoCompilingReport
  | ScormWrapper
  | VttTranslationReport;

const useFileService = (): [
  (reportInput: ReportType) => Promise<UploadResult>,
  number,
  (filename: string) => Promise<DownloadResult>,
  () => Promise<GetResult>,
  (filename: string) => Promise<DeleteResult>,
  boolean,
  (fileName: string, ftpFolder: string) => Promise<UploadResult>,
  (fileName: string) => Promise<UploadResult>,
  (fileName: string, ftpFolder: string) => Promise<UploadResult>,
] => {
  const axiosPrivate = useAxiosPrivate();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progressUpdate, setProgressUpdate] = useState(0);

  // Helper function to handle upload progress
  const handleUploadProgress = (progressEvent: AxiosProgressEvent) => {
    const { loaded, total } = progressEvent;
    const percent = Math.floor((loaded * 100) / total!);
    setProgressUpdate(percent);
  };

  // Upload file function
  const uploadFile = async (reportInput: ReportType): Promise<UploadResult> => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('state', JSON.stringify(state));

      if ('invoiceNumber' in reportInput) {
        formData.append('invoiceNumber', reportInput.invoiceNumber);
      }
      if ('invoiceDate' in reportInput) {
        formData.append('invoiceDate', reportInput.invoiceDate!.toString());
      }
      if ('title' in reportInput) {
        formData.append('title', reportInput.title);
      }
      if ('languages' in reportInput) {
        const currentState = JSON.parse(JSON.stringify(state));
        currentState.languages = reportInput.languages;
        formData.set('state', JSON.stringify(currentState));
      }

      reportInput.files.forEach((file: Blob) => {
        formData.append('files', file as Blob);
      });

      const response = await axiosPrivate.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: handleUploadProgress,
      });

      setIsLoading(false);
      setProgressUpdate(0);

      if (response.status === 200) {
        return { success: true, message: response.data.message };
      }
      return { success: false, error: response.data };
    } catch (error: unknown) {
      setIsLoading(false);
      setProgressUpdate(0);

      // Check if this is an Axios error with a response
      if (error instanceof Error) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          // Return the actual error message from the server
          const responseData = axiosError.response.data as Record<
            string,
            unknown
          >;
          return {
            success: false,
            error: (responseData.message as string) || String(responseData),
          };
        }
      }

      return { success: false, error: 'Error uploading file.' };
    }
  };

  // Download file function
  const downloadFile = async (filename: string): Promise<DownloadResult> => {
    setIsLoading(true);

    try {
      const response = await axiosPrivate.get(
        `/files/${filename}?reportId=${state.id}`,
        {
          responseType: 'blob',
        },
      );

      setIsLoading(false);

      if (response.status === 200) {
        return { success: true, data: response.data };
      }

      return { success: false, error: response.statusText };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Error downloading file.' };
    }
  };

  // Get files function
  const getFiles = async (): Promise<GetResult> => {
    setIsLoading(true);

    try {
      const response = await axiosPrivate.get(`/files?reportId=${state.id}`);

      setIsLoading(false);

      if (response.status === 200) {
        return { success: true, data: response.data };
      }

      return { success: false, error: response.statusText };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Error getting files.' };
    }
  };

  // Delete file function
  const deleteFile = async (filename: string): Promise<DeleteResult> => {
    setIsLoading(true);

    try {
      const response = await axiosPrivate.delete(
        `/files/${filename}?reportId=${state.id}`,
      );

      setIsLoading(false);

      if (response.status === 200) {
        return { success: true };
      }

      return { success: false, error: response.statusText };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Error deleting file.' };
    }
  };

  // Upload to FTP function
  const uploadToFtp = async (
    fileName: string,
    ftpFolder: string,
  ): Promise<UploadResult> => {
    setIsLoading(true);

    try {
      const response = await axiosPrivate.post(
        `/ftp/${fileName}?ftpFolder=${ftpFolder}`,
      );

      setIsLoading(false);

      if (response.status === 200) {
        return { success: true, message: response.data.message };
      }

      return { success: false, error: response.statusText };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Error uploading file.' };
    }
  };

  // Upload to manager function
  const uploadToManager = async (filename: string): Promise<UploadResult> => {
    setIsLoading(true);
    const reportId = state.id;

    try {
      const response = await axiosPrivate.post('/upload/manager', {
        filename,
        reportId,
      });

      setIsLoading(false);

      if (response.status === 200) {
        return { success: true, message: response.data.message };
      }

      return { success: false, error: response.statusText };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Error uploading file.' };
    }
  };

  // Manager upload to FTP function
  const managerUploadToFTP = async (
    fileName: string,
    ftpFolder: string,
  ): Promise<UploadResult> => {
    setIsLoading(true);

    // console.log('FTP UPLOAD', ftpFolder);
    try {
      const response = await axiosPrivate.post('/upload/ftp', {
        fileName,
        ftpFolder,
      });

      setIsLoading(false);

      if (response.status === 200) {
        return { success: true, message: response.data.message };
      }

      return { success: false, error: response.statusText };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Error uploading file.' };
    }
  };

  return [
    uploadFile,
    progressUpdate,
    downloadFile,
    getFiles,
    deleteFile,
    isLoading,
    uploadToFtp,
    uploadToManager,
    managerUploadToFTP,
  ];
};

export default useFileService;
